import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Box,
  TextField,
  Button,
  IconButton,
  Tabs,
  Tab,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
// Replace date picker imports with standard inputs
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from 'recharts';
import { 
  FileDownload,
  Print,
  ContentCopy,
  Refresh,
  Settings,
} from '@mui/icons-material';
import { OrgContext } from '../contexts/OrgContext';
import CurrencyFormatter from '../utils/CurrencyFormatter';
import formatDateToStandard from '../utils/DateFormatter';
// Remove date-fns import and handle dates with native JS methods
// import {getRevenueReports} from '../services/reportsService';
// Import the API client for API calls
import apiClient from '../api/apiClient';
import TanStackTable from '../components/TanStackTable';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import CustomDatePicker from '../components/CustomDatePicker';

// Custom tooltip component for charts
const CustomTooltip = ({ active, payload, label, type }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    
    if (type === 'profitability') {
      return (
        <Box sx={{ 
          bgcolor: 'background.paper',
          p: 2, 
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1
        }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {data.product_name}
          </Typography>
          <Typography variant="body2">
            Net Profit: ${Number(data.net_profit || 0).toFixed(2)}
          </Typography>
          <Typography variant="body2">
            Gross Profit: ${Number(data.gross_profit || 0).toFixed(2)}
          </Typography>
          <Typography variant="body2">
            Revenue: ${Number(data.total_revenue || 0).toFixed(2)}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            Click to view order details
          </Typography>
        </Box>
      );
    }
    
    return (
      <Box sx={{
        bgcolor: 'background.paper',
        p: 1.5,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        color: 'text.primary'
      }}>
        {type === 'inventory' ? (
          <>
            <p><strong>Product:</strong> {data.name}</p>
            <p><strong>Quantity:</strong> {data.value}</p>
            <p><strong>Category:</strong> {data.category}</p>
          </>
        ) : type === 'inventory-levels' ? (
          <>
            <p><strong>{data.name}</strong></p>
            <p><strong>Count:</strong> {data.value}</p>
            <p><strong>Products:</strong></p>
            <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
              {data.products?.map((product, idx) => (
                <li key={idx}>{product.name} ({product.quantity})</li>
              )) || 'No products'}
            </ul>
          </>
        ) : type === 'orders' ? (
          <>
            <p><strong>Date:</strong> {formatDateToStandard(data.date)}</p>
            <p><strong>Orders:</strong> {data.count}</p>
            <p><strong>Total Amount:</strong> <CurrencyFormatter value={data.amount} /></p>
          </>
        ) : type === 'orders-timeline' ? (
          (() => {
            const p = (payload && payload[0] && payload[0].payload) || {};
            const salesCount = p.salesCount ?? 0;
            const salesAmount = p.salesAmount ?? 0;
            const purchaseCount = p.purchaseCount ?? 0;
            const purchaseAmount = p.purchaseAmount ?? 0;
            return (
              <>
                <p><strong>Date:</strong> {formatDateToStandard(label)}</p>
                <p><strong>Sales Orders:</strong> {salesCount} orders</p>
                <p><strong>Sales Amount:</strong> <CurrencyFormatter value={salesAmount} /></p>
                <p><strong>Purchase Orders:</strong> {purchaseCount} orders</p>
                <p><strong>Purchase Amount:</strong> <CurrencyFormatter value={purchaseAmount} /></p>
              </>
            );
          })()
        ) : type === 'inventory-movements' ? (
          <>
            <p><strong>Date:</strong> {formatDateToStandard(label)}</p>
            {payload.map((entry, index) => (
              <p key={index} style={{ color: entry.color }}>
                <strong>{entry.name}:</strong> {entry.value} units
              </p>
            ))}
          </>
        ) : (
          <>
            <p><strong>{label}</strong></p>
            <p><strong>Value:</strong> {data.value}</p>
          </>
        )}
      </Box>
    );
  }
  return null;
};

// Helper function to truncate product names for x-axis
const truncateProductName = (name, maxLength = 12) => {
  if (!name) return '';
  if (name.length <= maxLength) return name;
  return name.substring(0, maxLength - 3) + '...';
};

// Custom X-Axis Label component with hover tooltip
const CustomXAxisLabel = ({ x, y, payload, maxLength = 12 }) => {
  const [isHovered, setIsHovered] = useState(false);
  const fullText = payload.value || '';
  const truncatedText = truncateProductName(fullText, maxLength);
  
  return (
    <g>
      <text
        x={x}
        y={y}
        dy={16}
        textAnchor="middle"
        fill="#666"
        fontSize="12"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{ cursor: 'pointer' }}
      >
        {truncatedText}
      </text>
      {isHovered && fullText !== truncatedText && (
        <>
          {/* Background rectangle for better visibility */}
          <rect
            x={x - (fullText.length * 3.5)}
            y={y + 25}
            width={fullText.length * 7}
            height={20}
            fill="rgba(0, 0, 0, 0.8)"
            rx={4}
            style={{ pointerEvents: 'none' }}
          />
          {/* Tooltip text */}
          <text
            x={x}
            y={y + 38}
            textAnchor="middle"
            fill="#fff"
            fontSize="11"
            fontWeight="bold"
            style={{
              pointerEvents: 'none'
            }}
          >
            {fullText}
          </text>
        </>
      )}
    </g>
  );
};

const Reports = () => {
  const { orgId } = useContext(OrgContext);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  
  // Use localStorage for persisting date ranges between refreshes
  const [startDate, setStartDate] = useState(() => {
    // Try to get stored dates from localStorage
    const storedStartDate = localStorage.getItem('reports_startDate');
    const storedPageLoadTime = localStorage.getItem('reports_pageLoadTime');
    
    // Check if we have stored values and if they're from a recent page load (within 10 minutes)
    if (storedStartDate && storedPageLoadTime) {
      const pageLoadTime = parseInt(storedPageLoadTime, 10);
      const currentTime = new Date().getTime();
      const timeDiff = currentTime - pageLoadTime;
      
      // If the stored date is from a page load within the last 10 minutes (600000ms), use it
      if (timeDiff < 600000) {
        try {
          return new Date(storedStartDate);
        } catch (e) {
          console.warn('Failed to parse stored start date', e);
        }
      }
    }
    
    // Default: today instead of 30 days ago
    return new Date();
  });
  
  const [endDate, setEndDate] = useState(() => {
    // Try to get stored dates from localStorage
    const storedEndDate = localStorage.getItem('reports_endDate');
    const storedPageLoadTime = localStorage.getItem('reports_pageLoadTime');
    
    // Check if we have stored values and if they're from a recent page load
    if (storedEndDate && storedPageLoadTime) {
      const pageLoadTime = parseInt(storedPageLoadTime, 10);
      const currentTime = new Date().getTime();
      const timeDiff = currentTime - pageLoadTime;
      
      // If the stored date is from a page load within the last 10 minutes, use it
      if (timeDiff < 600000) {
        try {
          return new Date(storedEndDate);
        } catch (e) {
          console.warn('Failed to parse stored end date', e);
        }
      }
    }
    
    // Default: today (unchanged)
    return new Date();
  });
  
  const [exportFormat, setExportFormat] = useState('csv');
  
  // Fiscal year settings
  const [fiscalYearMonth, setFiscalYearMonth] = useState(1); // Default: January (1)
  const [fiscalYearDay, setFiscalYearDay] = useState(1); // Default: 1st day
  const [timeRangeType, setTimeRangeType] = useState(() => {
    // Try to get stored time range type from localStorage
    const storedTimeRangeType = localStorage.getItem('reports_timeRangeType');
    const storedPageLoadTime = localStorage.getItem('reports_pageLoadTime');
    
    // Check if we have stored values and if they're from a recent page load
    if (storedTimeRangeType && storedPageLoadTime) {
      const pageLoadTime = parseInt(storedPageLoadTime, 10);
      const currentTime = new Date().getTime();
      const timeDiff = currentTime - pageLoadTime;
      
      // If the stored type is from a page load within the last 10 minutes, use it
      if (timeDiff < 600000) {
        return storedTimeRangeType;
      }
    }
    
    // Default: today instead of custom
    return 'today';
  });
  const [yearType, setYearType] = useState('calendar'); // Options: 'calendar', 'fiscal'
  const [openFiscalYearDialog, setOpenFiscalYearDialog] = useState(false);

  // State for reports data
  const [salesData, setSalesData] = useState({
    summary: {},
    timeline: [],
    topProducts: [],
    topCustomers: []
  });
  const [purchaseData, setPurchaseData] = useState({
    summary: {},
    timeline: [],
    topProducts: [],
    topSuppliers: []
  });
  const [inventoryData, setInventoryData] = useState({
    summary: {},
    levels: [],
    products: [],
    movements: {
      timelineData: [],
      products: []
    }
  });
  const [customerData, setCustomerData] = useState([]);
  const [supplierData, setSupplierData] = useState([]);
  // const [inventoryAnalytics, setInventoryAnalytics] = useState(null);

  // Ref for reports container to enable copy functionality
  const reportsContainerRef = useRef(null);

  // const [forecastDataMap, setForecastDataMap] = useState({});
  // const [expandedRow, setExpandedRow] = useState(null);

  const [leadTime, setLeadTime] = useState(7); // Default lead time in days
  const [forecastDays, setForecastDays] = useState(30); // Default forecast period in days

  const navigate = useNavigate(); // Initialize useNavigate

  const handleForecastInputChange = (field, value) => {
    if (field === 'leadTime') setLeadTime(value);
    if (field === 'forecastDays') setForecastDays(value);
  };

  const handleOpenPrintable = () => {
    navigate('/printable-reports', {
      state: {
        salesData,
        purchaseData,
        inventoryData,
      },
    });
  };

  // Example call for forecast data (could be triggered on row expand)
  /* const fetchForecastForItem = async (productId) => {
    try {
      // Adjust API path to match your backend route
      const res = await apiClient.get(`/reports/${orgId}/inventory-forecast`, {
        params: { productId, leadTimeDays: 7, futureDays: 14 },
      });
      setForecastDataMap((prev) => ({
        ...prev,
        [productId]: res.data,
      }));
    } catch (error) {
      console.error(error);
    }
  }; */

  // Render inventory rows with expandable forecast
  /* const renderInventoryRow = (product) => {
    const forecast = product.forecast || {};
    return (
      <>
        <TableRow key={product.id} hover onClick={() => handleRowExpand(product.id)}>
          <TableCell>{product.name}</TableCell>
          <TableCell>{product.sku}</TableCell>
          <TableCell>{product.category}</TableCell>
          <TableCell>{product.quantity}</TableCell>
          <TableCell>
            <CurrencyFormatter value={product.price} />
          </TableCell>
          <TableCell>
            <CurrencyFormatter value={product.price * product.quantity} />
          </TableCell>
          <TableCell>{product.status}</TableCell>
          <TableCell>{product.inventoryTurnover.toFixed(2)}</TableCell>
          <TableCell>{product.daysInInventory.toFixed(2)}</TableCell>
          <TableCell>{product.stockVelocity.toFixed(2)}</TableCell>
        </TableRow>
        {expandedRow === product.id && (
          <TableRow>
            <TableCell colSpan={10}>
              <Box sx={{ p: 2 }}>
                <Typography variant="body2">
                  Future Inventory Need: {forecast.futureInventoryNeed?.toFixed(2)}
                </Typography>
                <Typography variant="body2">
                  Demand Forecast: {forecast.demandForecast?.toFixed(2)}
                </Typography>
                <Typography variant="body2">
                  Safety Stock: {forecast.safetyStock}
                </Typography>
              </Box>
            </TableCell>
          </TableRow>
        )}
      </>
    );
  }; */

  // const handleRowExpand = (rowId) => {
  //   setExpandedRow((prev) => (prev === rowId ? null : rowId));
  // };

  // Time range options
  const timeRanges = [
    { value: 'custom', label: 'Custom Range' },
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'this_week', label: 'This Week' },
    { value: 'last_week', label: 'Last Week' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'this_quarter', label: 'This Quarter' },
    { value: 'last_quarter', label: 'Last Quarter' },
    { value: 'this_year', label: 'This Year' },
    { value: 'last_year', label: 'Last Year' },
  ];

  // Month options for fiscal year setting
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  // Generate days 1-31 for fiscal year day setting
  const days = Array.from({ length: 31 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}${getDaySuffix(i + 1)}`
  }));

  // Get day suffix (1st, 2nd, 3rd, etc.)
  function getDaySuffix(day) {
    if (day >= 11 && day <= 13) {
      return 'th';
    }
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }

  // Record current page load time on component mount
  useEffect(() => {
    // Save the current timestamp as the page load time
    localStorage.setItem('reports_pageLoadTime', new Date().getTime().toString());
    
    // Load fiscal year settings from localStorage
    const savedFiscalYearMonth = localStorage.getItem('fiscalYearMonth');
    const savedFiscalYearDay = localStorage.getItem('fiscalYearDay');
    const savedYearType = localStorage.getItem('reportYearType');
    
    if (savedFiscalYearMonth) {
      setFiscalYearMonth(parseInt(savedFiscalYearMonth, 10));
    }
    
    if (savedFiscalYearDay) {
      setFiscalYearDay(parseInt(savedFiscalYearDay, 10));
    }
    
    if (savedYearType) {
      setYearType(savedYearType);
    }
    
    // Clean up function - will run when component unmounts
    return () => {
      // We'll keep the stored values for a period of time (managed by the pageLoadTime)
      // This allows refreshes to maintain state but navigating away and back will reset
    };
  }, []);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Format date for API requests with validation (YYYY-MM-DD format required by backend)
  const formatDateForAPI = (date) => {
    try {
      if (!date || isNaN(date.getTime())) {
        // Return current date if invalid
        return new Date().toISOString().split('T')[0];
      }
      return date.toISOString().split('T')[0]; // YYYY-MM-DD format for API
    } catch (error) {
      console.warn('Invalid date provided to formatDateForAPI:', date);
      return new Date().toISOString().split('T')[0];
    }
  };

  // Safe date formatting for HTML date input display (YYYY-MM-DD format required by HTML)
  /* const formatDateForDisplay = (date) => {
    try {
      if (!date || isNaN(date.getTime())) {
        return '';
      }
      // Use local date components for HTML date input (YYYY-MM-DD format)
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.warn('Invalid date provided to formatDateForDisplay:', date);
      return '';
    }
  }; */

  // Calculate date range based on the selected time range type
  const calculateDateRange = (rangeType) => {
    const now = new Date();
    console.log('Current date:', now.getDate()); // Your debug log
    
    // Create dates in local timezone - this respects the user's system timezone
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let start = new Date(startDate);
    let end = new Date(endDate);

    // Get fiscal year start date for the current year
    const getCurrentFiscalYearStart = () => {
      const year = now.getFullYear();
      const month = fiscalYearMonth - 1; // JavaScript months are 0-indexed
      
      // If current date is before fiscal year start, use previous year's start
      if (
        now.getMonth() < month ||
        (now.getMonth() === month && now.getDate() < fiscalYearDay)
      ) {
        return new Date(year - 1, month, fiscalYearDay);
      }
      
      return new Date(year, month, fiscalYearDay);
    };

    // Get dates based on time range
    switch (rangeType) {
      case 'today':
        // Use exact local date - no timezone conversion
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        console.log('Today start date:', start.getDate()); // Debug log
        break;
      
      case 'yesterday':
        // Get yesterday in local timezone
        const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
        start = yesterday;
        end = yesterday;
        console.log('Yesterday date:', yesterday.getDate()); // Your debug log
        break;
      
      case 'this_week':
        start = new Date(today);
        // Get Monday of current week (getDay() returns 0 for Sunday, 1 for Monday, etc.)
        const dayOfWeek = start.getDay();
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // If Sunday (0), go back 6 days to Monday
        start.setDate(start.getDate() - daysFromMonday);
        end = today;
        break;
      
      case 'last_week':
        start = new Date(today);
        // Get Monday of current week first
        const currentDayOfWeek = start.getDay();
        const daysFromCurrentMonday = currentDayOfWeek === 0 ? 6 : currentDayOfWeek - 1;
        start.setDate(start.getDate() - daysFromCurrentMonday);
        // Then go back one more week to get last Monday
        start.setDate(start.getDate() - 7);
        end = new Date(start);
        end.setDate(end.getDate() + 6); // Sunday of last week
        break;
      
      case 'this_month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = today;
        break;
      
      case 'last_month':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      
      case 'this_quarter':
        const currentQuarter = Math.floor(today.getMonth() / 3);
        start = new Date(today.getFullYear(), currentQuarter * 3, 1);
        end = today;
        break;
      
      case 'last_quarter':
        const lastQuarter = Math.floor(today.getMonth() / 3) - 1;
        const yearOfLastQuarter = lastQuarter < 0 ? today.getFullYear() - 1 : today.getFullYear();
        const actualLastQuarter = lastQuarter < 0 ? 3 : lastQuarter;
        start = new Date(yearOfLastQuarter, actualLastQuarter * 3, 1);
        end = new Date(yearOfLastQuarter, (actualLastQuarter + 1) * 3, 0);
        break;
      
      case 'this_year':
        if (yearType === 'fiscal') {
          // Fiscal year
          start = getCurrentFiscalYearStart();
          end = today;
        } else {
          // Calendar year
          start = new Date(today.getFullYear(), 0, 1);
          end = today;
        }
        break;
      
      case 'last_year':
        if (yearType === 'fiscal') {
          // Last fiscal year
          const fiscalStart = getCurrentFiscalYearStart();
          start = new Date(fiscalStart);
          start.setFullYear(start.getFullYear() - 1);
          end = new Date(fiscalStart);
          end.setDate(end.getDate() - 1);
        } else {
          // Last calendar year
          start = new Date(today.getFullYear() - 1, 0, 1);
          end = new Date(today.getFullYear() - 1, 11, 31);
        }
        break;
      
      default: // custom - keep current start and end dates
        break;
    }
    
    // Only add 1 day if it's NOT today or yesterday (keep their exact local dates)
    start.setDate(start.getDate());
    end.setDate(end.getDate());
    
    return { start, end };
  };

  // Handle time range selection
  const handleTimeRangeChange = (event) => {
    const newRangeType = event.target.value;
    setTimeRangeType(newRangeType);
    
    // Save to localStorage
    localStorage.setItem('reports_timeRangeType', newRangeType);
    
    if (newRangeType !== 'custom') {
      const { start, end } = calculateDateRange(newRangeType);
      setStartDate(start);
      setEndDate(end);
      
      // Save to localStorage
      localStorage.setItem('reports_startDate', start.toISOString());
      localStorage.setItem('reports_endDate', end.toISOString());
    }
  };

  // Handle year type selection (fiscal or calendar)
  const handleYearTypeChange = (event) => {
    const newYearType = event.target.value;
    setYearType(newYearType);
    localStorage.setItem('reportYearType', newYearType);
    
    // Update date range if needed
    if (['this_year', 'last_year'].includes(timeRangeType)) {
      const { start, end } = calculateDateRange(timeRangeType);
      setStartDate(start);
      setEndDate(end);
    }
  };

  // Save fiscal year settings
  const handleSaveFiscalYearSettings = () => {
    localStorage.setItem('fiscalYearMonth', fiscalYearMonth.toString());
    localStorage.setItem('fiscalYearDay', fiscalYearDay.toString());
    
    // Update date range if using fiscal year
    if (yearType === 'fiscal' && ['this_year', 'last_year'].includes(timeRangeType)) {
      const { start, end } = calculateDateRange(timeRangeType);
      setStartDate(start);
      setEndDate(end);
    }
    
    setOpenFiscalYearDialog(false);
  };

  // Replace the service functions with direct API calls using apiClient
  const getSalesReports = async (orgId, startDate, endDate) => {
    try {
      const response = await apiClient.get(`/reports/sales/${orgId}`, {
        params: { startDate, endDate }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching sales reports:', error);
      return {
        summary: {},
        timeline: [],
        topProducts: [],
        topCustomers: [],
        orders: []
      };
    }
  };

  const getPurchaseReports = async (orgId, startDate, endDate) => {
    try {
      const response = await apiClient.get(`/reports/purchases/${orgId}`, {
        params: { startDate, endDate }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching purchase reports:', error);
      return {
        summary: {},
        timeline: [],
        topProducts: [],
        topSuppliers: []
      };
    }
  };

  const getInventoryReports = async (orgId, startDate, endDate) => {
    try {
      const response = await apiClient.get(`/reports/inventory/${orgId}`, {
        params: { startDate, endDate }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching inventory reports:', error);
      return {
        summary: {},
        levels: [],
        products: []
      };
    }
  };

  const getInventoryMovementReports = async (orgId, startDate, endDate, limit = 20) => {
    try {
      const response = await apiClient.get(`/reports/inventory-movements/${orgId}`, {
        params: { startDate, endDate, limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching inventory movement reports:', error);
      return {
        timelineData: [],
        products: []
      };
    }
  };

  const getCustomerReports = async (orgId, startDate, endDate) => {
    try {
      const response = await apiClient.get(`/reports/customers/${orgId}`, {
        params: { startDate, endDate }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching customer reports:', error);
      return [];
    }
  };

  const getSupplierReports = async (orgId, startDate, endDate) => {
    try {
      const response = await apiClient.get(`/reports/suppliers/${orgId}`, {
        params: { startDate, endDate }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching supplier reports:', error);
      return [];
    }
  };

  // Fetch all report data
  const fetchReportData = async () => {
    setLoading(true);
    try {
      const formattedStartDate = formatDateForAPI(startDate);
      const formattedEndDate = formatDateForAPI(endDate);
      
      // Fetch all reports in parallel using the direct API calls
      const [
        salesReports,
        purchaseReports,
        inventoryReports,
        movementReports,
        customerReports,
        supplierReports
      ] = await Promise.all([
        getSalesReports(orgId, formattedStartDate, formattedEndDate),
        getPurchaseReports(orgId, formattedStartDate, formattedEndDate),
        getInventoryReports(orgId, formattedStartDate, formattedEndDate),
        getInventoryMovementReports(orgId, formattedStartDate, formattedEndDate),
        getCustomerReports(orgId, formattedStartDate, formattedEndDate),
        getSupplierReports(orgId, formattedStartDate, formattedEndDate)
      ]);
      
      setSalesData(salesReports);
      setPurchaseData(purchaseReports);
      setInventoryData({ 
        ...inventoryReports,
        movements: movementReports
      });
      setCustomerData(customerReports);
      setSupplierData(supplierReports);
    } catch (error) {
      console.error('Error fetching reports data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    if (orgId) {
      fetchReportData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]); // intentionally only on org change

  // Handle date range change and refresh
  const handleRefresh = () => {
    // Refresh data using current date range settings
    fetchReportData();
    
    // Make sure our localStorage values are updated to match current state
    localStorage.setItem('reports_startDate', startDate.toISOString());
    localStorage.setItem('reports_endDate', endDate.toISOString());
    localStorage.setItem('reports_timeRangeType', timeRangeType);
    localStorage.setItem('reports_pageLoadTime', new Date().getTime().toString());
  };

  // Enhanced handler for manual date changes with validation
  const handleDateChange = (field, value) => {
    // When user manually changes a date, switch to custom range
    if (timeRangeType !== 'custom') {
      setTimeRangeType('custom');
      localStorage.setItem('reports_timeRangeType', 'custom');
    }
    
    try {
      // Validate the date input
      const newDate = new Date(value);
      
      // Check if the date is valid
      if (isNaN(newDate.getTime())) {
        console.warn('Invalid date input:', value);
        return; // Don't update state with invalid date
      }
      
      // Update the corresponding date field
      if (field === 'start') {
        setStartDate(newDate);
        localStorage.setItem('reports_startDate', newDate.toISOString());
      } else if (field === 'end') {
        setEndDate(newDate);
        localStorage.setItem('reports_endDate', newDate.toISOString());
      }
    } catch (error) {
      console.error('Error handling date change:', error);
      // Optionally show user feedback here
    }
  };

  // No longer need the date field click handler or date picker open handler
  // since we're always allowing edits to the date fields

  // Fiscal Year Settings Dialog
  const renderFiscalYearDialog = () => (
    <Dialog 
      open={openFiscalYearDialog} 
      onClose={() => setOpenFiscalYearDialog(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Fiscal Year Settings</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Set the start date of your fiscal year. This will be used for fiscal year-based reports.
        </DialogContentText>
        
        <Box sx={{ mt: 2 }}>
          <FormControl fullWidth margin="normal">
            <InputLabel id="fiscal-month-label">Month</InputLabel>
            <Select
              labelId="fiscal-month-label"
              value={fiscalYearMonth}
              label="Month"
              onChange={(e) => setFiscalYearMonth(e.target.value)}
            >
              {months.map((month) => (
                <MenuItem key={month.value} value={month.value}>
                  {month.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth margin="normal">
            <InputLabel id="fiscal-day-label">Day</InputLabel>
            <Select
              labelId="fiscal-day-label"
              value={fiscalYearDay}
              label="Day"
              onChange={(e) => setFiscalYearDay(e.target.value)}
            >
              {days.map((day) => (
                <MenuItem key={day.value} value={day.value}>
                  {day.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenFiscalYearDialog(false)}>Cancel</Button>
        <Button onClick={handleSaveFiscalYearSettings} color="primary" variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );

  function buildCsvData(salesData, purchaseData, inventoryData) {
    const rows = [];

    rows.push(['--- Sales Summary ---']);
    rows.push(['Total Amount', salesData?.summary?.totalAmount || 0]);
    rows.push(['Total Orders', salesData?.summary?.totalOrders || 0]);
    rows.push(['Average Order Value', salesData?.summary?.averageOrderValue || 0]);
    rows.push(['Total Units Sold', salesData?.summary?.totalUnitsSold || 0]);
    rows.push([]);

    rows.push(['--- Top Customers ---']);
    (salesData?.topCustomers || []).forEach((cust) => {
      rows.push([
        cust.name,
        cust.orderCount,
        cust.totalAmount,
        cust.averageOrderValue,
        cust.lastOrderDate,
      ]);
    });
    rows.push([]);

    rows.push(['--- Top Products (Sales) ---']);
    (salesData?.topProducts || []).forEach((prod) => {
      rows.push([prod.name, prod.quantity, prod.amount]);
    });
    rows.push([]);

    rows.push(['--- Purchase Summary ---']);
    rows.push(['Total Amount', purchaseData?.summary?.totalAmount || 0]);
    rows.push(['Total Orders', purchaseData?.summary?.totalOrders || 0]);
    rows.push([]);
    
    rows.push(['--- Top Products (Purchases) ---']);
    (purchaseData?.topProducts || []).forEach((prod) => {
      rows.push([prod.name, prod.quantity, prod.amount]);
    });
    rows.push([]);

    rows.push(['--- Inventory Summary ---']);
    rows.push(['Item Count', inventoryData?.summary?.totalUniqueItems || 0]);
    rows.push(['Total Quantity', inventoryData?.summary?.totalQuantity || 0]);
    rows.push(['Total Value', inventoryData?.summary?.totalValue || 0]);
    rows.push([]);

    const csvContent = rows.map(r => r.join(',')).join('\n');
    return csvContent;
  }

  const handleExport = async () => {
    const csvContent = buildCsvData(salesData, purchaseData, inventoryData);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const date = new Date().toISOString().replace(/[:.]/g, '-');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `report_export_${date}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopyData = () => {
    if (reportsContainerRef.current) {
      // Create a selection and copy the text content
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(reportsContainerRef.current);
      selection.removeAllRanges();
      selection.addRange(range);
      document.execCommand('copy');
      selection.removeAllRanges();
      
      alert('Report data copied to clipboard!');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // const scrollToTable = () => {
  //   document.getElementById('reportTable')?.scrollIntoView({ behavior: 'smooth' });
  // };

  const scrollToProductsSoldTable = () => {
    document.getElementById('productsSoldTable')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToUnitsPurchasedTable = () => {
    document.getElementById('unitsPurchasedTable')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Common styles
  const COLORS = ['#FF8042', '#00C49F', '#FFBB28', '#0088FE', '#8884d8'];
  const cardStyle = { 
    backgroundColor: 'background.paper',
    height: '100%', 
    minHeight: '200px',
    backdropFilter: 'saturate(140%) blur(6px)',
    border: '1px solid',
    borderColor: 'divider'
  };
  const cardHeaderStyle = { 
    borderBottom: '1px solid',
    borderColor: 'divider',
    padding: '16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  };

  // Example usage in the "Products Sold" section:
  const renderProductsSold = () => {
    if (!salesData?.unitsSoldBreakdown) return 'No data';
    return (
      <Box
        sx={{
          maxHeight: '150px',
          overflowY: 'scroll',
          p: 1,
          bgcolor: 'background.paper',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          cursor: 'pointer',
          textDecoration: 'underline',
        }}
        onClick={scrollToProductsSoldTable} // Add onClick handler
      >
        {salesData.unitsSoldBreakdown.map((item, index) => (
          <Typography
            key={index}
            variant="body2"
            sx={{ mb: '4px' }}
          >
            {item.quantity} {item.unit}
          </Typography>
        ))}
      </Box>
    );
  };

  const renderUnitsBreakdown = (unitsBreakdown) => {
    if (!unitsBreakdown || unitsBreakdown.length === 0) return 'No data';
    return (
      <Box
        sx={{
          maxHeight: '150px',
          overflowY: 'scroll',
          p: 1,
          bgcolor: 'background.paper',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          cursor: 'pointer',
          textDecoration: 'underline',
        }}
        onClick={scrollToUnitsPurchasedTable} // Add onClick handler
      >
        {unitsBreakdown.map((item, index) => (
          <Typography
            key={index}
            variant="body2"
            sx={{ mb: '4px' }}
          >
            {item.quantity} {item.unit}
          </Typography>
        ))}
      </Box>
    );
  };

  const salesDetailsColumns = [
    { header: 'Date', accessorKey: 'orderDate', cell: ({ getValue }) => formatDateToStandard(new Date(getValue())) },
    { header: 'Order ID', accessorKey: 'id' },
    { header: 'Customer', accessorKey: 'customerName' },
    { header: 'Products', accessorKey: 'productCount' },
    { 
      header: 'Amount', 
      accessorKey: 'amount',
      cell: ({ getValue }) => <CurrencyFormatter value={getValue()} />
    },
    { header: 'Status', accessorKey: 'status' },
  ];

  const topCustomersColumns = [
    { header: 'Customer', accessorKey: 'name' },
    { header: 'Orders', accessorKey: 'orderCount' },
    { header: 'Total Amount', accessorKey: 'totalAmount', cell: ({ getValue }) => <CurrencyFormatter value={getValue()} /> },
    { header: 'Average Order Value', accessorKey: 'averageOrderValue', cell: ({ getValue }) => <CurrencyFormatter value={getValue()} /> },
    { header: 'Last Order Date', accessorKey: 'lastOrderDate', cell: ({ getValue }) => formatDateToStandard(new Date(getValue())) },
  ];

  const purchaseOrdersColumns = [
    { header: 'Date', accessorKey: 'orderDate', cell: ({ getValue }) => formatDateToStandard(new Date(getValue())) },
    { header: 'Order ID', accessorKey: 'id' },
    { header: 'Supplier', accessorKey: 'supplierName' },
    { header: 'Products', accessorKey: 'productCount' },
    { header: 'Amount', accessorKey: 'amount', cell: ({ getValue }) => <CurrencyFormatter value={getValue()} /> },
    { header: 'Status', accessorKey: 'status' },
  ];

  const inventoryListColumns = [
    { header: 'Product', accessorKey: 'name' },
    { header: 'SKU', accessorKey: 'sku' },
    { header: 'Category', accessorKey: 'category' },
    { header: 'Quantity', accessorKey: 'quantity' },
    { header: 'Price', accessorKey: 'price', cell: ({ getValue }) => <CurrencyFormatter value={getValue()} /> },
    { header: 'Value', accessorKey: 'value', cell: ({ getValue }) => <CurrencyFormatter value={getValue()} /> },
    { header: 'Status', accessorKey: 'status' },
    { header: 'Inventory Turnover', accessorKey: 'inventoryTurnover' },
    { header: 'Days in Inventory', accessorKey: 'daysInInventory' },
    { header: 'Stock Velocity', accessorKey: 'stockVelocity' },
  ];

  // const components = { CurrencyFormatter };

  const unitsPurchasedColumns = [
    { header: 'Product ID', accessorKey: 'id' },
    { header: 'Product Name', accessorKey: 'name' },
    { header: 'Quantity', accessorKey: 'quantity' },
    { header: 'Unit', accessorKey: 'unit' },
  ];

  const productsSoldColumns = [
    { header: 'ID', accessorKey: 'id' },
    { header: 'Product Name', accessorKey: 'name' },
    { header: 'Quantity', accessorKey: 'quantity' },
    { header: 'Unit', accessorKey: 'unit' },
  ];

  /* const renderExpandableRow = (row) => {
    if (expandedRow === row.id) {
      return (
        <Box sx={{ mt: 2, pl: 4 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Products:
          </Typography>
          <ul>
            {row.products.map((product, index) => (
              <li key={index}>
                {product.name}: {product.quantity} {product.unit}
              </li>
            ))}
          </ul>
        </Box>
      );
    }
    return null;
  }; */

  /* const unitsSoldColumnDefs = [
    { headerName: 'Unit', field: 'unit', sortable: true, filter: true },
    { headerName: 'Quantity', field: 'quantity', sortable: true, filter: true },
  ]; */

  /* const unitsSoldBreakdownColumns = [
    { headerName: 'Product ID', field: 'id' },
    { headerName: 'Product Name', field: 'name' },
  ]; */

  // Transform unitsSoldBreakdown to flatten the products array for the table
  const transformedProductsSoldData = (salesData.unitsSoldBreakdown || []).flatMap((unitData) =>
    unitData.products.map((product) => ({
      id: product.id,
      name: product.name,
      quantity: product.quantity,
      unit: product.unit,
    }))
  );

  // Transform unitsPurchasedBreakdown to flatten the products array for the table
  const transformedUnitsPurchasedData = (purchaseData.unitsPurchasedBreakdown || []).flatMap((unitData) =>
    unitData.products.map((product) => ({
      id: product.id,
      name: product.name,
      quantity: product.quantity,
      unit: product.unit,
    }))
  );

  // const [revenueData, setRevenueData] = useState([]);
  const [profitabilityData, setProfitabilityData] = useState({});
  const [topProfitableData, setTopProfitableData] = useState([]);
  const [leastProfitableData, setLeastProfitableData] = useState([]);
  const [selectedProductOrders, setSelectedProductOrders] = useState(null);
  // const [loadingRevenue, setLoadingRevenue] = useState(false);
  const [loadingProfitability, setLoadingProfitability] = useState(false);
  const [loadingCharts, setLoadingCharts] = useState(false);

  // const fetchRevenueData = async () => { ... };

  const fetchProfitabilityData = async () => {
    setLoadingProfitability(true);
    try {
      console.log('[Reports.js:fetchProfitabilityData] Fetching profitability data with parameters:', {
        orgId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        method: 'lifo', // Hardcoded to LIFO
      });

      const response = await apiClient.get('/reports/profitability', {
        params: {
          org_id: orgId,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          method: 'lifo', // Always use LIFO
        }
      });
      
      const data = response.data;
      console.log('[Reports.js:fetchProfitabilityData] Raw API response:', data);
      
      // store full API response
      setProfitabilityData(data);
      
      // Update localStorage with current dates to ensure consistency
      localStorage.setItem('reports_startDate', startDate.toISOString());
      localStorage.setItem('reports_endDate', endDate.toISOString());
      localStorage.setItem('reports_timeRangeType', timeRangeType);
    } catch (error) {
      console.error('[Reports.js:fetchProfitabilityData] Error fetching profitability data:', error);
      setProfitabilityData({});
    } finally {
      setLoadingProfitability(false);
    }
  };

  const fetchTopProfitableData = async () => {
    setLoadingCharts(true);
    try {
      const response = await apiClient.get(`/reports/top-profitable/${orgId}`, {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          limit: 10,
          method: 'lifo',
        }
      });
      // Backend now returns the data directly (not wrapped in topProfitable)
      setTopProfitableData(response.data || []);
    } catch (error) {
      console.error('Error fetching top profitable data:', error);
      setTopProfitableData([]);
    }
  };

  const fetchLeastProfitableData = async () => {
    try {
      const response = await apiClient.get(`/reports/least-profitable/${orgId}`, {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          limit: 10,
          method: 'lifo',
        }
      });
      // Backend now returns the data directly (not wrapped in leastProfitable)
      setLeastProfitableData(response.data || []);
    } catch (error) {
      console.error('Error fetching least profitable data:', error);
      setLeastProfitableData([]);
    } finally {
      setLoadingCharts(false);
    }
  };

  const fetchProductOrders = async (productId) => {
    try {
      const response = await apiClient.get(`/reports/product-orders/${orgId}/${productId}`, {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        }
      });
      setSelectedProductOrders(response.data);
    } catch (error) {
      console.error('Error fetching product orders:', error);
      setSelectedProductOrders(null);
    }
  };

  // Use useCallback to memoize the fetchProfitabilityData function
  // const memoizedFetchProfitabilityData = React.useCallback(() => {
  //   if (tabValue === 5 && orgId) {
  //     fetchProfitabilityData();
  //   }
  // }, [tabValue, startDate, endDate, orgId, timeRangeType, fetchProfitabilityData]);

  useEffect(() => {
    if (tabValue === 5) {
      fetchProfitabilityData();
      fetchTopProfitableData();
      fetchLeastProfitableData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, startDate, endDate]);

  const profitabilityColumns = [
    {
      accessorKey: 'yearMonth',
      header: 'Month',
      cell: ({ getValue }) => {
        const value = getValue();
        if (!value) return '';
        const [year, month] = value.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1);
        return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
      }
    },
    {
      accessorKey: 'product_id',
      header: 'Product ID',
    },
    {
      accessorKey: 'product_name',
      header: 'Product Name',
    },
    {
      accessorKey: 'total_quantity_sold',
      header: 'Quantity',
      cell: ({ getValue }) => {
        const value = getValue();
        return value != null ? Number(value).toFixed(0) : '0';
      }
    },
    {
      accessorKey: 'total_revenue',
      header: 'Revenue',
      cell: ({ getValue }) => {
        const value = getValue();
        return value != null ? `$${Number(value).toFixed(2)}` : '$0.00';
      }
    },
    {
      accessorKey: 'total_cogs',
      header: 'COGS',
      cell: ({ getValue }) => {
        const value = getValue();
        return value != null ? `$${Number(value).toFixed(2)}` : '$0.00';
      }
    },
    {
      accessorKey: 'gross_profit',
      header: 'Gross Profit',
      cell: ({ getValue }) => {
        const value = getValue();
        return value != null ? `$${Number(value).toFixed(2)}` : '$0.00';
      }
    },
    {
      accessorKey: 'gross_margin',
      header: 'Gross Margin',
      cell: ({ getValue }) => {
        const value = getValue();
        if (value == null || isNaN(value)) return '0.00%';
        return `${(Number(value) * 100).toFixed(2)}%`;
      }
    },
    {
      accessorKey: 'inventory_financing_cost',
      header: 'Inv Fin. Cost',
      cell: ({ getValue }) => {
        const value = getValue();
        return value != null ? `$${Number(value).toFixed(2)}` : '$0.00';
      }
    },
    {
      accessorKey: 'ar_financing_cost',
      header: 'AR Fin. Cost',
      cell: ({ getValue }) => {
        const value = getValue();
        return value != null ? `$${Number(value).toFixed(2)}` : '$0.00';
      }
    },
    {
      accessorKey: 'total_financing_cost',
      header: 'Total Fin. Cost',
      cell: ({ getValue }) => {
        const value = getValue();
        return value != null ? `$${Number(value).toFixed(2)}` : '$0.00';
      }
    },
    {
      accessorKey: 'net_profit',
      header: 'Net Profit',
      cell: ({ getValue }) => {
        const value = getValue();
        return value != null ? `$${Number(value).toFixed(2)}` : '$0.00';
      }
    },
    {
      accessorKey: 'net_margin',
      header: 'Net Margin',
      cell: ({ getValue }) => {
        const value = getValue();
        if (value == null || isNaN(value)) return '0.00%';
        return `${(Number(value) * 100).toFixed(2)}%`;
      }
    },
    {
      accessorKey: 'avg_inventory_days',
      header: 'Inv Days',
      cell: ({ getValue }) => {
        const value = getValue();
        if (value == null || isNaN(value)) return '0.0';
        return Number(value).toFixed(1);
      }
    },
    {
      accessorKey: 'avg_payable_days',
      header: 'A/P Days',
      cell: ({ getValue }) => {
        const value = getValue();
        if (value == null || isNaN(value)) return '0.0';
        return Number(value).toFixed(1);
      }
    },
    {
      accessorKey: 'avg_receivable_days',
      header: 'A/R Days',
      cell: ({ getValue }) => {
        const value = getValue();
        if (value == null || isNaN(value)) return '0.0';
        return Number(value).toFixed(1);
      }
    },
  ];

  // Helper function to get row styling based on profitability
  const getRowStyling = (row, allRows) => {
    const netProfit = row.net_profit || 0;
    
    const sortedRows = [...allRows].sort((a, b) => (b.net_profit || 0) - (a.net_profit || 0));
    const isTop3 = sortedRows.slice(0, 3).some(topRow => 
      topRow.product_id === row.product_id && topRow.yearMonth === row.yearMonth
    );
    
    if (isTop3 && netProfit > 0) {
      return { 
        backgroundColor: 'rgba(67, 160, 71, 0.18)', 
        color: 'inherit',
        '& .MuiTableCell-root': {
          backgroundColor: 'rgba(67, 160, 71, 0.18)',
          color: 'inherit',
        }
      };
    } else if (netProfit < 0) {
      return { 
        backgroundColor: 'rgba(229, 57, 53, 0.22)', 
        color: 'inherit',
        '& .MuiTableCell-root': {
          backgroundColor: 'rgba(229, 57, 53, 0.22)',
          color: 'inherit',
        }
      };
    }
    return {
      backgroundColor: 'transparent',
      color: 'inherit',
      '& .MuiTableCell-root': {
        backgroundColor: 'transparent',
        color: 'inherit',
      }
    };
  };

  // Render the profitability table based on inventory method
  const renderProfitabilityTable = () => {
    // must have the full API response object
    if (!profitabilityData || !profitabilityData.monthlyProfitability) {
      return <Typography>No data available</Typography>;
    }

    // flatten months → product rows, attach yearMonth
    const rows = profitabilityData.monthlyProfitability
      .flatMap(month => (month.products || []).map(prod => ({
        yearMonth: month.yearMonth,
        ...prod
      })));

    if (rows.length === 0) {
      return <Typography>No profitability data available</Typography>;
    }

    // sort by date asc then product name
    rows.sort((a, b) => {
      if (a.yearMonth < b.yearMonth) return -1;
      if (a.yearMonth > b.yearMonth) return 1;
      return (a.product_name || '').localeCompare(b.product_name || '');
    });

    return (
      <>
        {/* Color Legend */}
        <Box sx={{ mb: 2, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ 
              width: 16, 
              height: 16, 
              backgroundColor: '#43a047',
              borderRadius: 1 
            }} />
            <Typography variant="body2">Top 3 Most Profitable</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ 
              width: 16, 
              height: 16, 
              backgroundColor: '#e53935',
              borderRadius: 1 
            }} />
            <Typography variant="body2">Negative Profitability</Typography>
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="h6">Profitability Breakdown</Typography>
          <Typography variant="body2" color="text.secondary">
            {`Showing ${rows.length} entries over ${new Set(rows.map(r=>r.yearMonth)).size} periods`}
          </Typography>
        </Box>
    <Card sx={{ mb: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
          <CardContent>
      <Typography variant="body2" paragraph sx={{ color: 'warning.light' }}>
              <strong>Important:</strong> Financing rate used: {((profitabilityData.organizationFinancingRate||0)).toFixed(2)}% per annum. If you have a different rate, please adjust in the settings menu.
            </Typography>
          </CardContent>
        </Card>
        {/* wrap table in overflow container */}
        <Box sx={{ 
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden'
        }}>
          <TanStackTable
            data={rows}
            columns={profitabilityColumns}
            getRowClassName={(row) => getRowStyling(row, rows)}
          />
        </Box>
      </>
    );
  };

  const renderProfitabilityCharts = () => {
    if (loadingCharts) {
      return <CircularProgress />;
    }

    return (
      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Top Profitable Items Chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6">Top Profitable Items</Typography>
            </Box>
            <CardContent sx={{ height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={topProfitableData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <XAxis 
                    dataKey="product_name" 
                    tick={<CustomXAxisLabel maxLength={12} />}
                    height={80}
                    interval={0}
                  />
                  <YAxis />
                  <ChartTooltip content={<CustomTooltip type="profitability" />} />
                  <Bar 
                    dataKey="net_profit" 
                    fill="#00C49F" 
                    name="Net Profit"
                    onClick={(data) => fetchProductOrders(data.product_id)}
                    style={{ cursor: 'pointer' }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Least Profitable Items Chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6">Least Profitable Items</Typography>
            </Box>
            <CardContent sx={{ height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={leastProfitableData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <XAxis 
                    dataKey="product_name" 
                    tick={<CustomXAxisLabel maxLength={12} />}
                    height={80}
                    interval={0}
                  />
                  <YAxis />
                  <ChartTooltip content={<CustomTooltip type="profitability" />} />
                  <Bar 
                    dataKey="net_profit" 
                    fill="#FF8042" 
                    name="Net Profit"
                    onClick={(data) => fetchProductOrders(data.product_id)}
                    style={{ cursor: 'pointer' }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderProductOrdersTable = () => {
    if (!selectedProductOrders) {
      return null;
    }

    const orderColumns = [
      { accessorKey: 'orderId', header: 'Order ID' },
      { 
        accessorKey: 'orderDate', 
        header: 'Date',
        cell: ({ getValue }) => new Date(getValue()).toLocaleDateString()
      },
      { accessorKey: 'quantity', header: 'Quantity' },
      { 
        accessorKey: 'unitPrice', 
        header: 'Unit Price',
        cell: ({ getValue }) => `$${Number(getValue()).toFixed(2)}`
      },
      { 
        accessorKey: 'totalPrice', 
        header: 'Total Price',
        cell: ({ getValue }) => `$${Number(getValue()).toFixed(2)}`
      },
      { accessorKey: 'orderStatus', header: 'Status' },
      { accessorKey: 'paymentStatus', header: 'Payment' },
    ];

    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Order Details for Selected Product
        </Typography>
        
        {selectedProductOrders.salesOrders.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, color: '#43a047' }}>
              Sales Orders ({selectedProductOrders.salesOrders.length})
            </Typography>
            <TanStackTable
              data={selectedProductOrders.salesOrders}
              columns={orderColumns}
            />
          </Box>
        )}

        {selectedProductOrders.purchaseOrders.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, color: '#ff9800' }}>
              Purchase Orders ({selectedProductOrders.purchaseOrders.length})
            </Typography>
            <TanStackTable
              data={selectedProductOrders.purchaseOrders}
              columns={orderColumns}
            />
          </Box>
        )}

        <Button 
          variant="outlined" 
          onClick={() => setSelectedProductOrders(null)}
          sx={{ mt: 2 }}
        >
          Close Order Details
        </Button>
      </Box>
    );
  };

  return (
    <Paper sx={{ p: 3, bgcolor: 'background.paper', color: 'text.primary', backdropFilter: 'saturate(140%) blur(6px)', border: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' }, 
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', md: 'center' },
        mb: 3
      }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Reports & Analytics
        </Typography>
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mt: { xs: 2, md: 0 },
          width: { xs: '100%', md: 'auto' }
        }}>
          {/* Date Range Options */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel id="time-range-label">Time Period</InputLabel>
              <Select
                labelId="time-range-label"
                value={timeRangeType}
                label="Time Period"
                onChange={handleTimeRangeChange}
                size="small"
              >
                {timeRanges.map((range) => (
                  <MenuItem key={range.value} value={range.value}>
                    {range.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {(timeRangeType === 'this_year' || timeRangeType === 'last_year') && (
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel id="year-type-label">Year Type</InputLabel>
                <Select
                  labelId="year-type-label"
                  value={yearType}
                  label="Year Type"
                  onChange={handleYearTypeChange}
                  size="small"
                >
                  <MenuItem value="calendar">Calendar</MenuItem>
                  <MenuItem value="fiscal">Fiscal</MenuItem>
                </Select>
              </FormControl>
            )}
            
            {yearType === 'fiscal' && (
              <Tooltip title="Fiscal Year Settings">
                <IconButton 
                  color="primary" 
                  onClick={() => setOpenFiscalYearDialog(true)}
                  size="small"
                >
                  <Settings />
                </IconButton>
              </Tooltip>
            )}

            {/* Date inputs - always enabled */}
            <CustomDatePicker
              label="Start Date"
              value={startDate}
              onChange={(date) => handleDateChange('start', date)}
            />
            
            <CustomDatePicker
              label="End Date"
              value={endDate}
              onChange={(date) => handleDateChange('end', date)}
            />
          </Box>
          
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<Refresh />} 
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Current Date Range Display */}
      <Box sx={{ mb: 2 }}>
  <Typography variant="body2" color="text.secondary">
          Showing data from: <b>{formatDateToStandard(startDate)}</b> to <b>{formatDateToStandard(endDate)}</b>
          {yearType === 'fiscal' && (timeRangeType === 'this_year' || timeRangeType === 'last_year') && (
            <> (Fiscal Year: {months[fiscalYearMonth - 1].label} {fiscalYearDay}{getDaySuffix(fiscalYearDay)})</>
          )}
        </Typography>        {/* Add a date format footnote */}
  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
          Note: All dates are in dd-MMM-yyyy format
        </Typography>
      </Box>

      {/* Report Controls - Tabs and Export Options */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2
      }}>
        <Box sx={{ width: '100%', overflowX: { xs: 'auto', sm: 'visible' } }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            variant="scrollable"
            scrollButtons="auto"
            sx={{ 
              minWidth: { xs: '600px', sm: 'auto' },
              '& .MuiTabs-indicator': { backgroundColor: 'primary.main' },
              '& .Mui-selected': { color: 'text.primary !important' },
              '& .MuiTab-root': { color: 'text.secondary' }
            }}
          >
          <Tab label="Sales" />
          <Tab label="Purchases" />
          <Tab label="Inventory" />
          <Tab label="Customers" />
          <Tab label="Suppliers" />
          <Tab label="Profitability" />
          </Tabs>
        </Box>

        <Box sx={{ display: 'none' }}> {/* Hide export section */}
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel id="export-format-label">Format</InputLabel>
            <Select
              labelId="export-format-label"
              value={exportFormat}
              label="Format"
              onChange={(e) => setExportFormat(e.target.value)}
            >
              <MenuItem value="csv">CSV</MenuItem>
              <MenuItem value="xlsx">Excel</MenuItem>
              <MenuItem value="pdf">PDF</MenuItem>
            </Select>
          </FormControl>

          <Tooltip title="Download Report">
            <Button 
              variant="outlined" 
              color="primary" 
              startIcon={<FileDownload />}
              onClick={handleExport}
            >
              Export
            </Button>
          </Tooltip>
        </Box>

        <Box sx={{ display: 'none' }}> {/* Hide print, copy, and printable view */}
          <Tooltip title="Copy Report Data">
            <IconButton color="primary" onClick={handleCopyData}>
              <ContentCopy />
            </IconButton>
          </Tooltip>

          <Tooltip title="Print Report">
            <IconButton color="primary" onClick={handlePrint}>
              <Print />
            </IconButton>
          </Tooltip>

          <Button onClick={handleOpenPrintable}>Open Printable View</Button>
        </Box>
      </Box>

      {/* Main report content area */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box ref={reportsContainerRef} className="print-container">
          {/* SALES REPORT */}
          {tabValue === 0 && (
            <Grid container spacing={3}>
              {/* Summary Cards */}
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={cardStyle}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                          Total Sales
                        </Typography>
                        <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                          <CurrencyFormatter value={salesData.summary.totalAmount || 0} />
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={cardStyle}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                          Orders Count
                        </Typography>
                        <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                          {salesData.summary.totalOrders || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={cardStyle}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                          Average Order Value
                        </Typography>
                        <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                          <CurrencyFormatter value={salesData.summary.averageOrderValue || 0} />
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={cardStyle}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                          Products Sold
                        </Typography>
                        {renderProductsSold()}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>

              {/* Sales Timeline Chart */}
              <Grid item xs={12} md={8}>
                <Card sx={cardStyle}>
                  <Box sx={cardHeaderStyle}>
                    <Typography variant="h6">Sales Timeline</Typography>
                  </Box>
                  <CardContent sx={{ height: 350 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={salesData.timeline}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <XAxis 
                          dataKey="date" 
                          tickFormatter={(date) => formatDateToStandard(date)} 
                        />
                        <YAxis yAxisId="left" orientation="left" stroke="#00C49F" />
                        <YAxis yAxisId="right" orientation="right" stroke="#FF8042" />
                        <ChartTooltip content={<CustomTooltip type="orders-timeline" />} />
                        <Legend />
                        <Line 
                          yAxisId="left"
                          type="monotone" 
                          dataKey="salesAmount" 
                          name="Sales Amount" 
                          stroke="#00C49F" 
                          activeDot={{ r: 8 }} 
                        />
                        <Line 
                          yAxisId="right"
                          type="monotone" 
                          dataKey="salesCount" 
                          name="Order Count" 
                          stroke="#FF8042" 
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Top Products List */}
              <Grid item xs={12} md={4}>
                <Card sx={cardStyle}>
                  <Box sx={cardHeaderStyle}>
                    <Typography variant="h6">Top Products</Typography>
                  </Box>
                  <CardContent sx={{ height: 350, overflow: 'auto' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {(salesData.topProducts || []).slice(0, 10).map((product, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: 1.5,
                            borderRadius: 2,
                            backgroundColor: COLORS[index % COLORS.length] + '20',
                            border: `2px solid ${COLORS[index % COLORS.length]}`,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: COLORS[index % COLORS.length] + '40',
                              transform: 'translateX(5px)'
                            }
                          }}
                        >
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              backgroundColor: COLORS[index % COLORS.length],
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                              fontSize: '0.9rem',
                              mr: 2,
                              flexShrink: 0
                            }}
                          >
                            {index + 1}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 'bold',
                                color: '#ffffff',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {product.name} (<CurrencyFormatter value={product.amount} />)
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Top Customers */}
              <Grid item xs={12}>
                <Card sx={cardStyle}>
                  <Box sx={cardHeaderStyle}>
                    <Typography variant="h6">Top Customers</Typography>
                  </Box>
                  <CardContent>
                    <TanStackTable
                      data={salesData.topCustomers || []}
                      columns={topCustomersColumns}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Detailed Sales Data Table */}
              <Grid item xs={12}>
                <Card sx={cardStyle}>
                  <Box sx={cardHeaderStyle}>
                    <Typography variant="h6">Sales Details</Typography>
                  </Box>
                  <CardContent>
                    <TanStackTable
                      data={salesData.orders || []}
                      columns={salesDetailsColumns}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Products Sold Section */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Products Sold
                </Typography>
                <div id="productsSoldTable">
                  <TanStackTable
                    data={transformedProductsSoldData}
                    columns={productsSoldColumns}
                  />
                </div>
              </Grid>
            </Grid>
          )}

          {/* PURCHASE REPORT */}
          {tabValue === 1 && (
            <Grid container spacing={3}>
              {/* Purchase summary cards */}
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={cardStyle}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                          Total Purchases
                        </Typography>
                        <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                          <CurrencyFormatter value={purchaseData.summary?.totalAmount || 0} />
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={cardStyle}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                          Orders Count
                        </Typography>
                        <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                          {purchaseData.summary?.totalOrders || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={cardStyle}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                          Average Order Cost
                        </Typography>
                        <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                          <CurrencyFormatter value={purchaseData.summary?.averageOrderValue || 0} />
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={cardStyle}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                          Units Purchased
                        </Typography>
                        {renderUnitsBreakdown(purchaseData?.unitsPurchasedBreakdown)}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>

              {/* Purchase Timeline Chart */}
              <Grid item xs={12} md={8}>
                <Card sx={cardStyle}>
                  <Box sx={cardHeaderStyle}>
                    <Typography variant="h6">Purchase Timeline</Typography>
                  </Box>
                  <CardContent sx={{ height: 350 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={purchaseData.timeline || []}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <XAxis 
                          dataKey="date" 
                          tickFormatter={(date) => formatDateToStandard(date)} 
                        />
                        <YAxis yAxisId="left" orientation="left" stroke="#00C49F" />
                        <YAxis yAxisId="right" orientation="right" stroke="#FF8042" />
                        <ChartTooltip content={<CustomTooltip type="orders-timeline" />} />
                        <Legend />
                        <Line 
                          yAxisId="left"
                          type="monotone" 
                          dataKey="purchaseAmount" 
                          name="Purchase Amount" 
                          stroke="#00C49F" 
                          activeDot={{ r: 8 }} 
                        />
                        <Line 
                          yAxisId="right"
                          type="monotone" 
                          dataKey="purchaseCount" 
                          name="Order Count" 
                          stroke="#FF8042" 
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Top Products List for Purchases */}
              <Grid item xs={12} md={4}>
                <Card sx={cardStyle}>
                  <Box sx={cardHeaderStyle}>
                    <Typography variant="h6">Top Products</Typography>
                  </Box>
                  <CardContent sx={{ height: 350, overflow: 'auto' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {(purchaseData.topProducts || []).slice(0, 10).map((product, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: 1.5,
                            borderRadius: 2,
                            backgroundColor: COLORS[index % COLORS.length] + '20',
                            border: `2px solid ${COLORS[index % COLORS.length]}`,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: COLORS[index % COLORS.length] + '40',
                              transform: 'translateX(5px)'
                            }
                          }}
                        >
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              backgroundColor: COLORS[index % COLORS.length],
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                              fontSize: '0.9rem',
                              mr: 2,
                              flexShrink: 0
                            }}
                          >
                            {index + 1}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 'bold',
                                color: '#ffffff',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {product.name} (<CurrencyFormatter value={product.amount} />)
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Top Suppliers */}
              <Grid item xs={12}>
                <Card sx={cardStyle}>
                  <Box sx={cardHeaderStyle}>
                    <Typography variant="h6">Top Suppliers</Typography>
                  </Box>
                  <CardContent>
                    <TanStackTable
                      data={purchaseData.topSuppliers || []}
                      columns={topCustomersColumns}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Purchase Orders List */}
              <Grid item xs={12}>
                <Card sx={cardStyle}>
                  <Box sx={cardHeaderStyle}>
                    <Typography variant="h6">Purchase Orders</Typography>
                  </Box>
                  <CardContent>
                    <TanStackTable
                      data={purchaseData.orders || []}
                      columns={purchaseOrdersColumns}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Units Purchased Section */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Units Purchased
                </Typography>
                <div id="unitsPurchasedTable">
                  <TanStackTable
                    data={transformedUnitsPurchasedData}
                    columns={unitsPurchasedColumns}
                  />
                </div>
              </Grid>
            </Grid>
          )}

          {/* INVENTORY REPORT */}
          {tabValue === 2 && (
            <Grid container spacing={3}>
              {/* Inventory summary cards */}
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={cardStyle}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                          Total Products
                        </Typography>
                        <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                          {inventoryData.summary.totalProducts || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={cardStyle}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                          Total Units
                        </Typography>
                        <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                          {inventoryData.summary.totalUnits || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={cardStyle}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                          Low Stock Items
                        </Typography>
                        <Typography variant="h5" sx={{ fontSize: '1.8rem', color: '#ff6b6b' }}>
                          {inventoryData.summary.lowStockCount || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={cardStyle}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                          Stock Value
                        </Typography>
                        <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                          <CurrencyFormatter value={inventoryData.summary.totalValue || 0} />
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>

              {/* Inventory levels */}
              <Grid item xs={12} md={6}>
                <Card sx={cardStyle}>
                  <Box sx={cardHeaderStyle}>
                    <Typography variant="h6">Inventory Levels</Typography>
                  </Box>
                  <CardContent sx={{ height: 350 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={inventoryData.levels}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <XAxis dataKey="name" />
                        <YAxis />
                        <ChartTooltip content={<CustomTooltip type="inventory-levels" />} />
                        <Bar dataKey="value">
                          {inventoryData.levels.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Inventory movement timeline */}
              <Grid item xs={12} md={6}>
                <Card sx={cardStyle}>
                  <Box sx={cardHeaderStyle}>
                    <Typography variant="h6">Inventory Movements</Typography>
                  </Box>
                  <CardContent sx={{ height: 350 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={inventoryData.movements?.timelineData || []}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <XAxis 
                          dataKey="date_of_movement" 
                          tickFormatter={(date) => formatDateToStandard(date)} 
                        />
                        <YAxis />
                        <ChartTooltip content={<CustomTooltip type="inventory-movements" />} />
                        <Legend />
                        {(inventoryData.movements?.products || []).map((product, index) => (
                          <Line
                            key={product}
                            type="monotone"
                            dataKey={product}
                            name={product}
                            stroke={COLORS[index % COLORS.length]}
                            activeDot={{ r: 8 }}
                            connectNulls={true}  // Add this to connect non-null data points
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Full inventory list */}
              <Grid item xs={12}>
                <Card sx={cardStyle}>
                  <Box sx={cardHeaderStyle}>
                    <Typography variant="h6">Inventory List</Typography>
                  </Box>
                  <CardContent>
                    <Box sx={{ mb: 3 }}>
                      <TextField
                        label="Lead Time (days)"
                        type="number"
                        value={leadTime}
                        onChange={(e) => handleForecastInputChange('leadTime', e.target.value)}
                        size="small"
                        sx={{ mr: 2 }}
                      />
                      <TextField
                        label="Forecast Period (T in days)"
                        type="number"
                        value={forecastDays}
                        onChange={(e) => handleForecastInputChange('forecastDays', e.target.value)}
                        size="small"
                      />
                    </Box>
                    <TanStackTable
                      data={inventoryData.products || []}
                      columns={inventoryListColumns}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* CUSTOMERS REPORT */}
          {tabValue === 3 && (
            <Grid container spacing={3}>
              {/* Customer Summary Cards */}
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={cardStyle}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                          Total Customers
                        </Typography>
                        <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                          {customerData.summary?.totalCustomers || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={cardStyle}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                          Active Customers
                        </Typography>
                        <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                          {customerData.summary?.activeCustomers || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={cardStyle}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                          Total Sales
                        </Typography>
                        <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                          <CurrencyFormatter value={customerData.summary?.totalSales || 0} />
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={cardStyle}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                          Average Order Value
                        </Typography>
                        <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                          <CurrencyFormatter value={customerData.summary?.averageOrderValue || 0} />
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>

              {/* Customer Purchases by Category */}
              <Grid item xs={12} md={6}>
                <Card sx={cardStyle}>
                  <Box sx={cardHeaderStyle}>
                    <Typography variant="h6">Purchases by Category</Typography>
                  </Box>
                  <CardContent sx={{ height: 350 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={customerData.categoryBreakdown || []}
                          dataKey="totalAmount"
                          nameKey="category"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          fill="#8884d8"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {(customerData.categoryBreakdown || []).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <ChartTooltip formatter={(value) => <CurrencyFormatter value={value} />} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Monthly Sales Chart */}
              <Grid item xs={12} md={6}>
                <Card sx={cardStyle}>
                  <Box sx={cardHeaderStyle}>
                    <Typography variant="h6">Monthly Sales</Typography>
                  </Box>
                  <CardContent sx={{ height: 350 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={customerData.monthlySales || []}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <XAxis dataKey="month" />
                        <YAxis yAxisId="left" orientation="left" stroke="#00C49F" />
                        <YAxis yAxisId="right" orientation="right" stroke="#FF8042" />
                        <ChartTooltip formatter={(value, name) => {
                          return name === 'amount' 
                            ? <CurrencyFormatter value={value} />
                            : value;
                        }} />
                        <Legend />
                        <Bar yAxisId="left" dataKey="amount" name="Sales Amount" fill="#00C49F" />
                        <Bar yAxisId="right" dataKey="count" name="Order Count" fill="#FF8042" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Customer List */}
              <Grid item xs={12}>
                <Card sx={cardStyle}>
                  <Box sx={cardHeaderStyle}>
                    <Typography variant="h6">Customer Details</Typography>
                  </Box>
                  <CardContent>
                    <TanStackTable
                      data={customerData.customers || []}
                      columns={topCustomersColumns}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* SUPPLIERS REPORT */}
          {tabValue === 4 && (
            <Grid container spacing={3}>
              {/* Supplier Summary Cards */}
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={cardStyle}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                          Total Suppliers
                        </Typography>
                        <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                          {supplierData.summary?.totalSuppliers || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={cardStyle}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                          Active Suppliers
                        </Typography>
                        <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                          {supplierData.summary?.activeSuppliers || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={cardStyle}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                          Total Purchases
                        </Typography>
                        <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                          <CurrencyFormatter value={supplierData.summary?.totalPurchased || 0} />
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={cardStyle}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1 }}>
                          Average Order Value
                        </Typography>
                        <Typography variant="h5" sx={{ fontSize: '1.8rem' }}>
                          <CurrencyFormatter value={supplierData.summary?.averageOrderValue || 0} />
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>

              {/* Category Distribution */}
              <Grid item xs={12} md={6}>
                <Card sx={cardStyle}>
                  <Box sx={cardHeaderStyle}>
                    <Typography variant="h6">Product Categories</Typography>
                  </Box>
                  <CardContent sx={{ height: 350 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={supplierData.categoryBreakdown || []}
                          dataKey="totalAmount"
                          nameKey="category"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          fill="#8884d8"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {(supplierData.categoryBreakdown || []).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <ChartTooltip formatter={(value) => <CurrencyFormatter value={value} />} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Supplier List */}
              <Grid item xs={12}>
                <Card sx={cardStyle}>
                  <Box sx={cardHeaderStyle}>
                    <Typography variant="h6">Supplier Details</Typography>
                  </Box>
                  <CardContent>
                    <TanStackTable
                      data={supplierData.suppliers || []}
                      columns={topCustomersColumns}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* PROFITABILITY REPORT */}
          {tabValue === 5 && (
            <Box>
              {loadingProfitability ? (
                <CircularProgress />
              ) : (
                <>
                  {renderProfitabilityCharts()}
                  {renderProfitabilityTable()}
                  {renderProductOrdersTable()}
                </>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Fiscal Year Settings Dialog */}
      {renderFiscalYearDialog()}
    </Paper>
  );
};

export default Reports;
